import PaymentType from './PaymentType';
import SavedCreditCards from './SavedCreditCards';
import NewCreditCards from './NewCreditCards';
import OrderSummary from './OrderSummary';
import { CustomImage, Text, useText } from '@src/components';
import { Form } from 'antd';
import masterCardLogo from '@src/assets/images/cardOrders/mastercard-logo.svg';
import logoMobilExpress from '@src/assets/images/cardOrders/logo-mobilexpress.webp';
import logoAmex from '@src/assets/images/cardOrders/american-express-logo.png';
import logoVisa from '@src/assets/images/cardOrders/visa-inc-logo.svg';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import '@src/styles/cardOrder/payment-type.scss';
import { useEffect, useMemo, useReducer, useState } from 'react';
import { getCardWithVouList, setPaymentType } from '@src/store/slice/mobilExpressSlice';
import EftWireTransferCards from './EftWireTransferCards';
import BankContract from './BankContract';
import { useMediaQuery } from '@src/hooks/useMediaQuery';
import { VouIdCode } from '@src/store/enums';
import { useHistory } from 'react-router-dom';
import { confirmDialog } from '@src/components/CustomDialog';
import {
  loadBalancesDelete,
  setRunTimeTotalPrice,
  resetCurrentLoadBalance,
  setOrderStep,
} from '@src/store/slice/balanceUploadSlice';

const initialState = {
  savedCreditCards: false,
  newCreditCards: false,
};

function formVisibleReducer(state, action) {
  switch (action.type) {
    case 'savedCreditCards':
      return {
        savedCreditCards: true,
        newCreditCards: false,
      };
    case 'newCardAddFrom':
      return {
        savedCreditCards: false,
        newCreditCards: true,
      };
    default:
      return initialState;
  }
}

const Payment = ({ isCompliments = false, isMarket = false, vouId: propVouId }) => {
  const dispatch = useDispatch();
  const { paymentType } = useSelector((state) => state?.mobilExpress);
  const { currentLoadBalance } = useSelector((state) => state?.balanceUpload);
  const { currentBank } = useSelector((state) => state?.bank);
  const [counter, setCounter] = useState(0);
  const isMobil = useMediaQuery({ query: '(max-width: 991.98px)' });
  const { isSchool } = useSelector((state) => state?.auth);
  const { customerVou } = useSelector((state) => state?.customer);

  const [form] = Form.useForm();

  const [formVisible, visibleDispatch] = useReducer(formVisibleReducer, initialState, (state) => {
    return {
      savedCreditCards: false,
      newCreditCards: false,
    };
  });

  const vouId = useMemo(() => {
    if (propVouId !== undefined && propVouId !== null) return propVouId;
    if (currentLoadBalance?.vouId !== undefined && currentLoadBalance?.vouId !== null) {
      return currentLoadBalance.vouId;
    }
    if (isMarket) return VouIdCode.Market;
    if (isCompliments) return VouIdCode.Compliments;
    return VouIdCode.Restaurant;
  }, [currentLoadBalance?.vouId, isMarket, isCompliments, propVouId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const history = useHistory();
  const [isExit, setIsExit] = useState(false);

  const warningHtmlContent = <Text t="whenYouLeaveThisPageTheInformationYou" />;
  const giveUpText = useText('giveUp');

  useEffect(() => {
    const historyBlock = history.block((location) => {
      if (isExit) {
        return true;
      }
      if (
        currentLoadBalance?.id &&
        currentLoadBalance?.vouId === VouIdCode.Compliments &&
        currentLoadBalance.pendingApproval !== true
      ) {
        confirmDialog({
          title: <Text t="attention" />,
          htmlContent: warningHtmlContent,
          okText: <Text t="yes" />,
          cancelText: <Text t={giveUpText} />,
          closeIcon: false,
          onCancel: async (e) => {
            setIsExit(false);
            return false;
          },
          onOk: async () => {
            if (currentLoadBalance?.id > 0) {
              const body = { id: currentLoadBalance?.id };
              await dispatch(loadBalancesDelete(body));
            }
            setIsExit(true);
            await dispatch(setOrderStep(null));
            await dispatch(resetCurrentLoadBalance());
            await dispatch(setRunTimeTotalPrice(0));
            history.push(location.pathname);
          },
        });
      } else {
        return true;
      }

      return false;
    });

    return historyBlock;
  }, [history, isExit, dispatch, currentLoadBalance]);

  useEffect(() => {
    (async () => {
      if (paymentType === 1) {
        const action = await dispatch(
          getCardWithVouList({
            VouId: vouId,
          }),
        );
        if (getCardWithVouList.fulfilled.match(action)) {
          const array = action.payload?.data?.cardList;
          if (!Array.isArray(array)) {
            console.error('Stored credit card response does not contain a card list.', action.payload);
            visibleDispatch({ type: 'newCardAddFrom' });
            return;
          }
          if (array.length === 0) {
            visibleDispatch({ type: 'newCardAddFrom' });
          } else {
            visibleDispatch({ type: 'savedCreditCards' });
          }
        } else {
          visibleDispatch({ type: 'newCardAddFrom' });
        }
      } else if (paymentType === 2) {
        visibleDispatch({ type: 'savedCreditCards' });
      }
    })();
  }, [dispatch, paymentType, vouId]);

  useEffect(() => {
    if (customerVou?.pamId === 4 && isSchool) {
      dispatch(setPaymentType(1));
    }
  }, [customerVou, isSchool]);

  return (
    <div className="payment-order-content">
      <div className="payment-content">
        <div className="payment-type-content">
          {!isSchool ? <PaymentType /> : null}

          {paymentType === 1 ? (
            <>
              {formVisible?.savedCreditCards && paymentType && !isSchool && (
                <SavedCreditCards
                  counter={counter}
                  setCounter={setCounter}
                  handleAnotherCreditCard={() => visibleDispatch({ type: 'newCardAddFrom' })}
                />
              )}
              {(formVisible?.newCreditCards || isSchool) && (
                <NewCreditCards
                  form={form}
                  formVisible={formVisible}
                  counter={counter}
                  setCounter={setCounter}
                  handleRegisteredCreditCard={() => visibleDispatch({ type: 'savedCreditCards' })}
                />
              )}
            </>
          ) : (
            <>
              {currentBank && currentBank !== 'paymentLater' ? (
                <BankContract />
              ) : (
                <EftWireTransferCards type={'balance'} />
              )}
            </>
          )}
          {currentLoadBalance?.paymentType !== 5 && !isMobil && (
            <>
              <hr />
              <div className="footer-logo">
                <div className="left-logos">
                  <CustomImage className="logoMobil" src={logoMobilExpress} />
                  <span className={'left-logos-text'}>
                    <Text t="yourCardsAreStoredSecurelyInThePci" />
                  </span>
                </div>
                <div className="right-logos">
                  <CustomImage src={logoAmex} style={{ width: '50px', height: '50px' }} />
                  <CustomImage src={logoVisa} />
                  <CustomImage src={masterCardLogo} />
                </div>
              </div>
            </>
          )}
        </div>

        <OrderSummary
          counter={counter}
          setCounter={setCounter}
          form={form}
          formVisible={formVisible?.newCreditCards}
        />
      </div>
    </div>
  );
};
Payment.propTypes = {
  isCompliments: PropTypes.bool,
  isMarket: PropTypes.bool,
  vouId: PropTypes.number,
};
export default Payment;
